/**
 * Content Manager V3 - Markdown-based
 * Manages support content from markdown files with frontmatter
 */

import categories from "../data/support/categories.json";
import metadata from "../data/support/metadata.json";
import { parseMarkdownFileSync } from "./markdownParser";
import { gettingStartedContent } from "../data/support/articles/getting-started-content.js";
import { faqContent } from "../data/support/articles/faq-content.js";
import { tournamentCreationContent } from "../data/support/articles/tournament-creation-content.js";
import { runATournamentContent } from "../data/support/articles/run-a-tournament-content.js";
import { runBracketsContent } from "../data/support/articles/run-brackets-content.js";

// Cache for loaded articles
let contentCache = null;

// Load all articles from markdown files
const loadArticles = () => {
  if (contentCache) return contentCache;

  try {
    const allArticles = [
      parseMarkdownFileSync(gettingStartedContent),
      parseMarkdownFileSync(faqContent),
      parseMarkdownFileSync(tournamentCreationContent),
      parseMarkdownFileSync(runATournamentContent),
      parseMarkdownFileSync(runBracketsContent),

      // Add more markdown content files here as they're created
    ];

    // Filter out articles from hidden categories
    const hiddenCategoryIds = categories
      .filter((category) => category.hidden)
      .map((category) => category.id);

    const articles = allArticles.filter(
      (article) => !hiddenCategoryIds.includes(article.category)
    );

    contentCache = articles;
    return articles;
  } catch (error) {
    console.error("Error loading articles:", error);
    return [];
  }
};

// Get all articles
export const getArticles = () => {
  return loadArticles();
};

// Get article by ID
export const getArticleById = (id) => {
  return getArticles().find((article) => article.id === id);
};

// Get article by path
export const getArticleByPath = (path) => {
  return getArticles().find((article) => article.path === path);
};

// Get articles by category
export const getArticlesByCategory = (categoryId) => {
  return getArticles().filter((article) => article.category === categoryId);
};

// Get articles by type
export const getArticlesByType = (type) => {
  return getArticles().filter((article) => article.type === type);
};

// Search articles
export const searchArticles = (query) => {
  const searchTerm = query.toLowerCase();
  return getArticles().filter(
    (article) =>
      article.title.toLowerCase().includes(searchTerm) ||
      article.description.toLowerCase().includes(searchTerm) ||
      (article.tags &&
        article.tags.some((tag) => tag.toLowerCase().includes(searchTerm))) ||
      (article.content &&
        article.content.markdown.toLowerCase().includes(searchTerm))
  );
};

// Get related articles
export const getRelatedArticles = (articleId, limit = 3) => {
  const currentArticle = getArticleById(articleId);
  if (!currentArticle) return [];

  const related = getArticles()
    .filter(
      (article) =>
        article.id !== articleId &&
        (article.category === currentArticle.category ||
          (article.tags &&
            currentArticle.tags &&
            article.tags.some((tag) => currentArticle.tags.includes(tag))))
    )
    .slice(0, limit);

  return related;
};

// Get popular articles
export const getPopularArticles = (limit = 5) => {
  return getArticles().slice(0, limit);
};

// Get all categories
export const getCategories = () => {
  return categories;
};

// Get category by ID
export const getCategoryById = (id) => {
  return categories.find((category) => category.id === id);
};

// Get metadata
export const getMetadata = () => {
  return metadata;
};

// Get articles by tag
export const getArticlesByTag = (tag) => {
  return getArticles().filter(
    (article) => article.tags && article.tags.includes(tag)
  );
};

// Get all tags
export const getAllTags = () => {
  const articles = getArticles();
  const allTags = new Set();

  articles.forEach((article) => {
    if (article.tags) {
      article.tags.forEach((tag) => allTags.add(tag));
    }
  });

  return Array.from(allTags).sort();
};

// Clear cache (useful for development)
export const clearCache = () => {
  contentCache = null;
};

export default {
  getArticles,
  getArticleById,
  getArticleByPath,
  getArticlesByCategory,
  getArticlesByType,
  searchArticles,
  getRelatedArticles,
  getPopularArticles,
  getCategories,
  getCategoryById,
  getMetadata,
  getArticlesByTag,
  getAllTags,
  clearCache,
};
